'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { CalendarDays, LayoutDashboard, ListChecks, LogOut, MessageSquare, User } from 'lucide-react';
import { SidebarBrandLogo } from '@/components/layout/SidebarBrandLogo';
import { startNavigationLoadingForHref } from '@/lib/navigation-loading';
import { useAuthStore } from '@/lib/auth-store';
import { getEffectiveRole } from '@/lib/rbac';

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const SECTIONS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Evaluate',
    items: [
      { href: '/jury', label: 'Overview', icon: LayoutDashboard },
      { href: '/jury/assigned-events', label: 'Assigned events', icon: CalendarDays },
      { href: '/jury/evaluations', label: 'My evaluations', icon: ListChecks },
      { href: '/jury/messages', label: 'Messages', icon: MessageSquare },
    ],
  },
  {
    title: 'Account',
    items: [{ href: '/jury/profile', label: 'Jury profile', icon: User }],
  },
];

function isNavActive(pathname: string | null, href: string) {
  const current = (pathname || '/').replace(/\/$/, '') || '/';
  const target = href.replace(/\/$/, '') || '/';
  if (target === '/jury') return current === '/jury';
  return current === target || current.startsWith(`${target}/`);
}

export function JurySidebar() {
  const pathname = usePathname();
  const router = useRouter();
  const { user, logout } = useAuthStore();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pendingHref, setPendingHref] = useState<string | null>(null);
  const role = getEffectiveRole(user);

  useEffect(() => {
    setPendingHref(null);
  }, [pathname]);

  const markPending = (href: string) => {
    const current = (pathname || '/').replace(/\/$/, '') || '/';
    const target = href.replace(/\/$/, '') || '/';
    if (target !== current) {
      startNavigationLoadingForHref(href, pathname);
      setPendingHref(href);
    }
  };

  return (
    <aside className="eh-sidebar ud-sidebar w-full overflow-hidden" aria-label="Jury dashboard">
      <div className="shrink-0 border-b border-white/10 px-5 pb-4 pt-6">
        <Link href="/" aria-label="EnthiosHub home">
          <SidebarBrandLogo />
        </Link>
      </div>

      <div ref={scrollRef} className="eh-sidebar-scroll custom-scrollbar px-1 pb-3">
        {SECTIONS.map((section) => (
          <div key={section.title} className="mb-1">
            <p className="ud-nav-label">{section.title}</p>
            <nav className="space-y-0.5" aria-label={section.title}>
              {section.items.map((item) => {
                const Icon = item.icon;
                const active = isNavActive(pendingHref || pathname, item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    prefetch
                    aria-current={active ? 'page' : undefined}
                    onClick={() => markPending(item.href)}
                    onMouseEnter={() => {
                      try {
                        router.prefetch(item.href);
                      } catch {
                        /* ignore */
                      }
                    }}
                    className="ud-nav-item"
                  >
                    <Icon className="h-5 w-5 shrink-0 text-text-inverse/85" aria-hidden />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </div>
        ))}
      </div>

      {user ? (
        <div className="shrink-0 border-t border-white/10 px-3 py-3">
          <div className="flex items-center gap-2">
            <Link
              href="/jury/profile"
              className="flex min-w-0 flex-1 items-center gap-3 rounded-xl px-2 py-2 transition-colors duration-150 hover:bg-bg/8"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--color-primary)]/25 text-sm font-bold uppercase text-text-inverse">
                {(user.name || user.email || 'U').slice(0, 1)}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-text-inverse">{user.name || 'Account'}</p>
                <p className="truncate text-[11px] capitalize text-text-inverse/72">{role || 'Jury'}</p>
              </div>
            </Link>
            <button
              type="button"
              aria-label="Sign out"
              onClick={async () => {
                try {
                  await logout();
                } catch {
                  /* still leave */
                }
                window.location.href = '/login?reason=logged_out';
              }}
              className="rounded-lg p-2 text-text-inverse/45 transition-colors duration-150 hover:bg-bg/10 hover:text-text-inverse"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
